import { useEffect, useState } from 'react';
import {
    breakpointQuery,
    matchesBreakpointAtSsr,
    type Breakpoint,
} from '../lib/breakpoints';

/**
 * "Is the viewport at or above this Tailwind breakpoint?", asked of the browser in
 * the SAME rem units the stylesheet uses. See `lib/breakpoints.ts` for why that
 * matters (item 1).
 *
 * ⚠️ HYDRATION CONTRACT, deliberately mirroring `useWindowSize`: the first render —
 * during SSG prerendering AND on the client's hydration pass — returns the SSR
 * answer, never the real viewport. The real value is applied in an effect, after
 * hydration. Reading `matchMedia` in the initial state instead would render
 * different HTML than was prerendered on all 4,202 pages (React error #418).
 *
 * SIBLING, NOT A SUBSTITUTE: `matchesBreakpointUp` in `lib/breakpoints.ts` is the
 * imperative read for event handlers, and does NOT subscribe to changes. Use this
 * hook when a RENDER depends on the breakpoint; use that one inside a handler that
 * already runs at the right moment.
 */
export function useBreakpointUp(bp: Breakpoint): boolean {
    const [matches, setMatches] = useState<boolean>(() => matchesBreakpointAtSsr(bp));

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

        const mq = window.matchMedia(breakpointQuery(bp));
        // Sync to the real viewport immediately after hydration, then track it.
        setMatches(mq.matches);

        const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, [bp]);

    return matches;
}
